import { useState } from 'react';

import {
  LABEL_CELL,
  dropColumnSpans,
  mergeDown,
  removeRow,
  setSpannedCell,
  spanLayout,
  unmerge,
} from '../../lib/pumsemTableSpan';
import type {
  PumsemResourceKind,
  PumsemTable,
  PumsemTableColumn,
  PumsemTableRow,
} from '../../model/pumsem.type';
import { BTN_GHOST, FIELD } from './pumsemFormStyles';

interface PumsemTableEditorProps {
  table: PumsemTable;
  onChange: (table: PumsemTable) => void;
}

const KIND_OPTIONS: { value: PumsemResourceKind; label: string }[] = [
  { value: 'LABOR', label: '인력' },
  { value: 'MATERIAL', label: '재료' },
  { value: 'EQUIPMENT', label: '장비' },
  { value: 'OTHER', label: '기타' },
];

/** 한 표의 열·행 상한(서버 `PUMSEM_LIMITS` 와 같은 크기)보다 넉넉하게. */
const MAX_KEY_ATTEMPTS = 1000;

/** 열·행 키는 사람이 안 짓는다. 겹치지 않고 짧으면 그만이다. */
const nextKey = (prefix: string, used: string[]): string => {
  for (let index = 1; index < MAX_KEY_ATTEMPTS; index += 1) {
    const candidate = `${prefix}${index}`;
    if (!used.includes(candidate)) return candidate;
  }
  return `${prefix}${Date.now()}`;
};

/** 열 위치 교환. 원본 순서와 어긋난 열을 옮길 때 쓴다. */
const moveAt = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [picked] = next.splice(from, 1);
  next.splice(to, 0, picked);
  return next;
};

/**
 * 품 표 한 장 편집기. 표 블록(`body` 안의 `type: 'table'`) 하나를 맡는다.
 *
 * ── 지켜야 할 것 ────────────────────────────────────────────────
 * 값은 **문자열 그대로** 담는다. `type="number"` 를 쓰거나 `Number()` 로
 * 정규화하면 `0.010` 이 `0.01` 이 되고, 표준품셈은 그 자릿수 자체가 규정이라
 * 다른 값이 된다. `-`·`별도계상` 같은 비수치 값도 그대로 들어와야 한다.
 *
 * 행 `key` 는 판을 가로지르는 짝짓기 축이라 **화면에 노출하지도, 바꾸지도
 * 않는다** — 라벨을 고쳐도 같은 행으로 남아야 개정 diff 가 삭제+신설로 잘못
 * 잡히지 않는다.
 */
export function PumsemTableEditor({ table, onChange }: PumsemTableEditorProps) {
  const [droppingColumn, setDroppingColumn] = useState<string | null>(null);
  const layout = spanLayout(table);

  const patch = (next: Partial<PumsemTable>) => onChange({ ...table, ...next });

  const patchRow = (rowIndex: number, next: Partial<PumsemTableRow>) =>
    patch({
      rows: table.rows.map((row, i) =>
        i === rowIndex ? { ...row, ...next } : row,
      ),
    });

  return (
    <div>
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_10rem_10rem_10rem]">
        <input
          className={FIELD}
          value={table.caption}
          placeholder="표 제목 (예: 호칭경별 품)"
          aria-label="표 제목"
          onChange={(event) => patch({ caption: event.target.value })}
        />
        <input
          className={FIELD}
          value={table.rowHeader ?? '구분'}
          placeholder="행축 이름 (종류)"
          aria-label="표 행축 이름"
          onChange={(event) => patch({ rowHeader: event.target.value })}
        />
        <input
          className={FIELD}
          value={table.columnHeader ?? ''}
          placeholder="열축 이름 (구분)"
          aria-label="표 열축 이름"
          onChange={(event) => patch({ columnHeader: event.target.value || null })}
        />
        <input
          className={FIELD}
          value={table.valueUnit ?? ''}
          placeholder="값 단위 (팀당)"
          aria-label="표 기준 단위"
          onChange={(event) =>
            patch({ valueUnit: event.target.value || null })
          }
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              <th className="w-52 px-2 py-1.5 text-left text-[12px] font-bold text-neutral-500">
                구분
              </th>
              <th className="w-24 px-2 py-1.5 text-left text-[12px] font-bold text-neutral-500">
                종류
              </th>
              {table.columns.map((column, columnIndex) => {
                const patchColumn = (next: Partial<PumsemTableColumn>) =>
                  patch({
                    columns: table.columns.map((c, i) =>
                      i === columnIndex ? { ...c, ...next } : c,
                    ),
                  });

                return (
                  <th key={column.key} className="space-y-1 px-1 py-1.5">
                    {/* 상위 헤더 — 이웃한 열에 같은 값을 적으면 한 칸으로
                        병합돼 발행본과 같은 2단 헤더가 된다. */}
                    <input
                      className={`${FIELD} bg-neutral-50 text-center text-[12px] dark:bg-white/5`}
                      value={column.group ?? ''}
                      placeholder="상위 헤더"
                      aria-label={`${columnIndex + 1}열 상위 헤더`}
                      onChange={(event) =>
                        patchColumn({ group: event.target.value || null })
                      }
                    />
                    <input
                      className={`${FIELD} text-center`}
                      value={column.label}
                      placeholder="15mm"
                      aria-label={`${columnIndex + 1}열 이름`}
                      onChange={(event) =>
                        patchColumn({ label: event.target.value })
                      }
                    />
                    <div className="flex justify-center gap-1 text-[11px] text-neutral-400">
                      <button
                        type="button"
                        aria-label={`${column.label || columnIndex + 1}열 왼쪽으로`}
                        disabled={columnIndex === 0}
                        className="hover:text-neutral-700 disabled:opacity-30"
                        onClick={() =>
                          patch({
                            columns: moveAt(
                              table.columns,
                              columnIndex,
                              columnIndex - 1,
                            ),
                          })
                        }
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        aria-label={`${column.label || columnIndex + 1}열 오른쪽으로`}
                        disabled={columnIndex === table.columns.length - 1}
                        className="hover:text-neutral-700 disabled:opacity-30"
                        onClick={() =>
                          patch({
                            columns: moveAt(
                              table.columns,
                              columnIndex,
                              columnIndex + 1,
                            ),
                          })
                        }
                      >
                        →
                      </button>
                      {/* 열 하나를 지우면 **모든 행의 그 값**이 함께 사라지고
                          되돌릴 수 없다. 행 삭제(한 줄)와 달리 2단계로 둔다. */}
                      {droppingColumn === column.key ? (
                        <button
                          type="button"
                          aria-label={`${column.label || columnIndex + 1}열 정말 삭제`}
                          className="font-bold text-danger"
                          onClick={() => {
                            setDroppingColumn(null);
                            const next = dropColumnSpans(table, column.key);
                            onChange({
                              ...next,
                              columns: next.columns.filter(
                                (_, i) => i !== columnIndex,
                              ),
                            });
                          }}
                        >
                          정말?
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label={`${column.label || columnIndex + 1}열 삭제`}
                          className="hover:text-danger"
                          onClick={() => setDroppingColumn(column.key)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={row.key}>
                {/* 화면과 같은 모양으로 합쳐 보여 준다 — 병합을 표시만 하면 어느
                    칸이 어디까지 덮는지 머릿속으로 맞춰야 한다. */}
                {layout[rowIndex][LABEL_CELL] > 0 && (
                  <SpanCell
                    table={table}
                    rowIndex={rowIndex}
                    cellKey={LABEL_CELL}
                    size={layout[rowIndex][LABEL_CELL]}
                    name={`${rowIndex + 1}행 구분`}
                    onChange={onChange}
                  >
                    <input
                      className={FIELD}
                      value={row.label}
                      placeholder="배관공"
                      aria-label={`${rowIndex + 1}행 구분`}
                      onChange={(event) =>
                        onChange(
                          setSpannedCell(
                            table,
                            rowIndex,
                            LABEL_CELL,
                            event.target.value,
                          ),
                        )
                      }
                    />
                  </SpanCell>
                )}
                <td className="px-1 py-1">
                  <select
                    className={FIELD}
                    value={row.kind}
                    aria-label={`${rowIndex + 1}행 종류`}
                    onChange={(event) =>
                      patchRow(rowIndex, {
                        kind: event.target.value as PumsemResourceKind,
                      })
                    }
                  >
                    {KIND_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                {table.columns.map((column) =>
                  layout[rowIndex][column.key] === 0 ? null : (
                    <SpanCell
                      key={column.key}
                      table={table}
                      rowIndex={rowIndex}
                      cellKey={column.key}
                      size={layout[rowIndex][column.key]}
                      name={`${row.label || rowIndex + 1} ${column.label}`}
                      onChange={onChange}
                    >
                      {/* type="number" 를 쓰지 않는다 — 0.010 이 0.01 이 된다. */}
                      <input
                        className={`${FIELD} text-right tabular-nums`}
                        value={row.values[column.key] ?? ''}
                        inputMode="decimal"
                        aria-label={`${row.label || rowIndex + 1} ${column.label}`}
                        onChange={(event) =>
                          onChange(
                            setSpannedCell(
                              table,
                              rowIndex,
                              column.key,
                              event.target.value || null,
                            ),
                          )
                        }
                      />
                    </SpanCell>
                  ),
                )}
                <td className="px-1 py-1 text-center">
                  <button
                    type="button"
                    aria-label={`${row.label || rowIndex + 1}행 삭제`}
                    className="text-neutral-400 hover:text-danger"
                    // 병합 시작 행을 지우면 병합을 다음 행으로 넘겨야 한다 —
                    // 그냥 빼면 아래 병합이 한 칸씩 밀려 엉뚱한 칸을 덮는다.
                    onClick={() => onChange(removeRow(table, rowIndex))}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={BTN_GHOST}
          onClick={() =>
            patch({
              rows: [
                ...table.rows,
                {
                  key: nextKey(
                    'r',
                    table.rows.map((row) => row.key),
                  ),
                  label: '',
                  kind: 'LABOR',
                  values: {},
                  change: 'UNCHANGED',
                  changedColumns: [],
                },
              ],
            })
          }
        >
          행 추가
        </button>
        <button
          type="button"
          className={BTN_GHOST}
          onClick={() =>
            patch({
              columns: [
                ...table.columns,
                {
                  key: nextKey(
                    'c',
                    table.columns.map((column) => column.key),
                  ),
                  label: '',
                  group: null,
                },
              ],
            })
          }
        >
          열 추가
        </button>
      </div>
    </div>
  );
}

/**
 * 세로 병합이 되는 칸. 시작 칸만 `rowSpan` 으로 그리고, 마우스를 올리거나
 * 포커스가 들어오면 병합 버튼이 보인다 — 늘 보이면 칸마다 버튼이 붙어 표가
 * 읽히지 않는다. 버튼은 DOM 에 늘 있어서 키보드로도 닿는다.
 */
function SpanCell({
  table,
  rowIndex,
  cellKey,
  size,
  name,
  onChange,
  children,
}: {
  table: PumsemTable;
  rowIndex: number;
  cellKey: string;
  size: number;
  name: string;
  onChange: (table: PumsemTable) => void;
  children: React.ReactNode;
}) {
  const canMerge = rowIndex + size < table.rows.length;
  return (
    <td
      rowSpan={size}
      className={`group px-1 py-1 align-middle ${size > 1 ? 'bg-brand-50/50 dark:bg-brand-500/5' : ''}`}
    >
      {children}
      <div className="mt-0.5 flex justify-end gap-1.5 text-[11px] text-neutral-400 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
        {size > 1 && (
          <button
            type="button"
            aria-label={`${name} 병합 해제`}
            className="hover:text-neutral-700"
            onClick={() => onChange(unmerge(table, rowIndex, cellKey))}
          >
            병합 해제
          </button>
        )}
        <button
          type="button"
          aria-label={`${name} 아래 칸과 병합`}
          disabled={!canMerge}
          className="hover:text-neutral-700 disabled:opacity-30"
          onClick={() => onChange(mergeDown(table, rowIndex, cellKey))}
        >
          ↧ 아래와 병합
        </button>
      </div>
    </td>
  );
}
